import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import logoAsset from "@/assets/coffman-cove-logo.png.asset.json";
import { supabase } from "@/integrations/supabase/client";
import { claimAdminRole, getCurrentStaffRole } from "@/lib/admin-access";
import { can } from "@/lib/staff-roles";

/** The role this account holds, claiming admin for allowlisted emails. */
async function resolveStaffRole() {
  const role = await getCurrentStaffRole();
  if (role) return role;
  return (await claimAdminRole()) ? ("admin" as const) : null;
}

export const Route = createFileRoute("/admin/")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Admin Login — Coffman Cove Lodging" },
      { name: "description", content: "Staff login for Coffman Cove Lodging site administration." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminLogin,
});

type ThrottleState = {
  locked: boolean;
  retry_after_seconds: number;
  attempts_remaining: number;
};

function formatWait(seconds: number) {
  const s = Math.max(1, Math.ceil(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rest = s % 60;
  return rest ? `${m}m ${rest}s` : `${m}m`;
}

function clientHint() {
  if (typeof navigator === "undefined") return null;
  const raw = `${navigator.userAgent}|${navigator.language}|${new Date().getTimezoneOffset()}`;
  let h = 0;
  for (let i = 0; i < raw.length; i += 1) {
    h = (h * 31 + raw.charCodeAt(i)) | 0;
  }
  return `ua_${(h >>> 0).toString(36)}`;
}

function AdminLogin() {
  const navigate = useNavigate();
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);
  const [lockSeconds, setLockSeconds] = useState(0);
  const [attemptsLeft, setAttemptsLeft] = useState<number | null>(null);

  useEffect(() => {
    if (lockSeconds <= 0) return;
    const id = window.setInterval(() => {
      setLockSeconds((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [lockSeconds]);

  function applyThrottle(state: ThrottleState | null) {
    if (!state) return;
    setLockSeconds(state.locked ? Math.max(1, state.retry_after_seconds) : 0);
    setAttemptsLeft(state.locked ? 0 : state.attempts_remaining);
  }

  useEffect(() => {
    const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
    if (params?.has("denied")) {
      setDenied(true);
    }
    if (params?.has("reset")) {
      toast.success("Password updated — you're signed in.");
    }
    void supabase.auth.getUser().then(async ({ data }) => {
      if (!data.user) return;
      const role = await resolveStaffRole();
      if (!role) {
        setDenied(true);
        return;
      }
      navigate({ to: can(role, "viewMoney") ? "/admin/dashboard" : "/admin/calendar" });
    });
  }, [navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (lockSeconds > 0) {
      toast.error(`Too many attempts. Try again in ${formatWait(lockSeconds)}.`);
      return;
    }
    setBusy(true);
    try {
      const { data: pre } = await supabase.rpc("admin_login_throttle_status", { _email: email });
      const preState = (pre as ThrottleState[] | null)?.[0] ?? null;
      if (preState?.locked) {
        applyThrottle(preState);
        toast.error(`Too many attempts. Try again in ${formatWait(preState.retry_after_seconds)}.`);
        return;
      }

      const { error } = await supabase.auth.signInWithPassword({ email, password });

      const { data: post } = await supabase.rpc("record_admin_login_attempt", {
        _email: email,
        _success: !error,
        _client_hint: clientHint() ?? undefined,
      });
      const postState = (post as ThrottleState[] | null)?.[0] ?? null;
      applyThrottle(postState);

      if (error) {
        if (postState?.locked) {
          toast.error(`Too many failed attempts. Sign-in is locked for ${formatWait(postState.retry_after_seconds)}.`);
        } else if (postState && postState.attempts_remaining <= 2) {
          toast.error(`Incorrect email or password. ${postState.attempts_remaining} attempt${postState.attempts_remaining === 1 ? "" : "s"} left before a temporary lockout.`);
        } else {
          toast.error(error.message);
        }
        return;
      }

      const role = await resolveStaffRole();
      if (!role) {
        toast.error("This account isn't approved for staff access.");
        return;
      }
      toast.success("Signed in");
      navigate({ to: can(role, "viewMoney") ? "/admin/dashboard" : "/admin/calendar" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  async function onForgotPassword() {
    if (!email) {
      toast.error("Enter your staff email first, then tap “Forgot password”.");
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      toast.success("Reset link sent. Check your email.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not send the reset link.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-8 shadow-sm">
        <div className="flex flex-col items-center text-center">
          <img
            src={logoAsset.url}
            alt="Coffman Cove Lodging"
            width={200}
            height={200}
            className="h-24 w-auto object-contain"
          />
          <div className="mt-3 flex items-center gap-2 text-[var(--color-ember)]">
            <ShieldCheck size={20} aria-hidden />
            <span className="text-xs font-semibold uppercase tracking-[0.25em]">Staff only</span>
          </div>
        </div>
        <h1 className="font-display text-2xl mt-3 text-center">Admin Login</h1>
        <p className="mt-2 text-sm text-muted-foreground text-center">
          Sign in to review photo submissions, reviews, and fishing reports.
        </p>

        {denied ? (
          <p className="mt-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            This account isn't approved for admin access. Sign in with an approved staff account.
          </p>
        ) : null}

        {lockSeconds > 0 ? (
          <p role="alert" className="mt-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            Too many failed sign-in attempts. Try again in{" "}
            <span className="font-semibold tabular-nums">{formatWait(lockSeconds)}</span>.
          </p>
        ) : attemptsLeft !== null && attemptsLeft <= 2 ? (
          <p className="mt-4 rounded-md border border-[var(--color-ember)]/40 bg-[var(--color-ember)]/10 px-3 py-2 text-sm">
            {attemptsLeft} attempt{attemptsLeft === 1 ? "" : "s"} left before a temporary lockout.
          </p>
        ) : null}

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div>
            <label htmlFor="admin-email" className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">
              Email
            </label>
            <input
              id="admin-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="block text-xs uppercase tracking-wider text-muted-foreground mb-1">
              Password
            </label>
            <input
              id="admin-password"
              type="password"
              required
              minLength={6}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
          <button
            type="submit"
            disabled={busy || lockSeconds > 0}
            className="w-full rounded-md bg-[var(--color-ember)] px-4 py-2.5 text-sm font-semibold uppercase tracking-wider text-[var(--color-ember-foreground)] disabled:opacity-60"
          >
            {busy ? "…" : lockSeconds > 0 ? `Locked — ${formatWait(lockSeconds)}` : "Sign in"}
          </button>
        </form>

        <div className="mt-4 text-center text-sm">
          <button
            type="button"
            disabled={busy}
            onClick={() => void onForgotPassword()}
            className="text-[var(--color-ember)] hover:underline disabled:opacity-60"
          >
            Forgot password?
          </button>
        </div>



        <p className="mt-6 text-center text-xs text-muted-foreground">
          Admin access is limited to approved staff emails.
        </p>
        <div className="mt-4 text-center">
          <Link to="/" className="text-xs text-muted-foreground hover:underline">
            ← Back to site
          </Link>
        </div>
      </div>
    </div>
  );
}
